import { FC, ReactNode, useContext } from "react";
import { useLocation, useParams } from "react-router";

import { SATOSLink } from "@/app/application/SATOSLink";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { sharedBreadcrumbsSx } from "@/styles/sharedNavStyles";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { Box, Breadcrumbs, Tooltip, Typography } from "@mui/material";

import { useFetchMaterialHandlingProcedure } from "@/service/MaterialHandlingQueries";
import { RouteParams } from "@/service/Utilities";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


const truncateSx = {
	display: "inline-flex" as const,
	alignItems: "center" as const,
	maxWidth: { xs: 100, sm: 140, md: 200 },
	whiteSpace: "nowrap" as const,
	overflow: "hidden" as const,
	textOverflow: "ellipsis" as const,
	verticalAlign: "middle" as const,
	fontSize: "0.875rem" as const,
};

type MaterialHandlingBreadcrumbsProps = {
	inline?: boolean;
};

export const MaterialHandlingBreadcrumbs: FC<
	MaterialHandlingBreadcrumbsProps
> = ({ inline = false }) => {
	const translation: Language | null = useContext(TranslationContext);
	const { pathname } = useLocation();
	const params = useParams<RouteParams & { id?: string; tagid?: string }>();

	// Fetch procedure for view/archive/edit to show TagId
	const procedureId =
		params.id && /^\d+$/.test(params.id) ? parseInt(params.id, 10) : undefined;
	const { data: procedure } = useFetchMaterialHandlingProcedure(
		procedureId,
		params.tagid ?? "",
	);
	const tagId = procedure?.TagId ?? params.tagid ?? params.id ?? "";

	const isMyPage = !!pathname.match(/\/materialhandling\/?(mypage)?$/);
	const isList = pathname.includes("/materialhandling/list");
	const isView = pathname.includes("/materialhandling/view/");
	const isArchive = !!pathname.match(/\/materialhandling\/archive\/\d+/);
	const isPending = pathname.includes("/materialhandling/approval/pending");
	const isApprove = pathname.includes("/materialhandling/approval/approve/");
	const isEdit = pathname.includes("/editv2");

	const crumbs: Array<{ label: ReactNode; to?: string; current?: boolean }> = [
		{
			label: (
				<Tooltip
					title={<Translate String="Home">Home</Translate>}
					enterDelay={600}
				>
					<Box
						component="span"
						sx={{ display: "inline-flex", alignItems: "center" }}
					>
						<SATOSLink to="/">
							<HomeRoundedIcon sx={{ fontSize: 20 }} />
						</SATOSLink>
					</Box>
				</Tooltip>
			),
			to: "/",
		},
		{
			label: (
				<Translate String="MaterialHandling.Breadcrumb">
					Material handling
				</Translate>
			),
			to: "/materialhandling/mypage",
			current:
				!isList &&
				!isView &&
				!isArchive &&
				!isPending &&
				!isApprove &&
				!isEdit &&
				isMyPage,
		},
	];

	if (isList) {
		crumbs.push({
			label: (
				<Translate String="MaterialHandling.Breadcrumb.AllProcedures">
					All procedures
				</Translate>
			),
			current: true,
		});
	} else if (isView) {
		crumbs.push(
			{
				label: (
					<Translate String="MaterialHandling.Breadcrumb.View">View</Translate>
				),
				to: "/materialhandling/mypage",
			},
			{
				label: tagId || (procedureId ? `#${procedureId}` : "..."),
				current: true,
			},
		);
	} else if (isArchive) {
		crumbs.push(
			{
				label: (
					<Translate String="MaterialHandling.Breadcrumb.Archive">
						Archive
					</Translate>
				),
				to: "/materialhandling/mypage",
			},
			{
				label: tagId || (procedureId ? `#${procedureId}` : "..."),
				current: true,
			},
		);
	} else if (isApprove) {
		crumbs.push(
			{
				label: (
					<Translate String="MaterialHandling.PendingApproval.Breadcrumb">
						Pending approval
					</Translate>
				),
				to: "/materialhandling/approval/pending",
			},
			{
				label: (
					<>
						<Translate String="MaterialHandling.Update.Approve.Header">
							Approve update
						</Translate>
						{tagId && ` · ${tagId}`}
					</>
				),
				current: true,
			},
		);
	} else if (isEdit) {
		crumbs.push(
			{
				label: (
					<Translate String="MaterialHandling.Breadcrumb.Edit">Edit</Translate>
				),
				to: "/materialhandling/mypage",
			},
			{
				label: tagId || (procedureId ? `#${procedureId}` : "..."),
				current: true,
			},
		);
	} else if (isPending) {
		crumbs.push({
			label: (
				<Translate String="MaterialHandling.PendingApproval.Breadcrumb">
					Pending approval
				</Translate>
			),
			current: true,
		});
	} else if (!isMyPage) {
		// Fallback for create, save, etc.
		crumbs.push({
			label: pathname.split("/").pop() || "Material handling",
			current: true,
		});
	}

	return (
		<Breadcrumbs
			aria-label={GetLanguageValueAsString(translation, "Auto.Features.MaterialHandling.Ui.Components.MaterialHandlingBreadcrumbs.L179C15.Breadcrumb", "breadcrumb")}
			separator="›"
			maxItems={4}
			itemsBeforeCollapse={1}
			itemsAfterCollapse={1}
			sx={{
				...sharedBreadcrumbsSx,
				py: inline ? 0 : undefined,
				minWidth: 0,
				overflow: "hidden",
				"& .MuiBreadcrumbs-separator": { fontSize: inline ? 13 : undefined },
				"& .MuiBreadcrumbs-ol": { alignItems: "center", flexWrap: "nowrap" },
				"& .MuiBreadcrumbs-li": { overflow: "hidden" },
			}}
		>
			{crumbs.map((crumb, idx) => {
				if (idx === 0) return <Box key={idx}>{crumb.label}</Box>;
				if (crumb.to && !crumb.current) {
					return (
						<SATOSLink key={idx} to={crumb.to}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									...truncateSx,
									fontSize: "0.875rem",
									"&:hover": {
										color: "primary.main",
										textDecoration: "underline",
									},
								}}
							>
								{crumb.label}
							</Typography>
						</SATOSLink>
					);
				}
				return (
					<Typography
						key={idx}
						variant="body2"
						color="text.primary"
						sx={{ ...truncateSx, fontWeight: 500 }}
						aria-current="page"
					>
						{crumb.label}
					</Typography>
				);
			})}
		</Breadcrumbs>
	);
};
